/** Plain-DOM transform editor for level authors. Gameplay stays in Simulation. */

import { tuning } from '@/core/tuning';
import {
  cloneLevelForEditing,
  listEditorTargets,
  nudgeEditorTarget,
  readEditorTarget,
  rotateEditorTarget,
  setEditorTransform,
  type EditorAxis,
  type EditorTarget,
} from '@/editor/levelEditorModel';
import type { LevelSaveResult } from '@/editor/levelSaveClient';
import type { LevelDef } from '@/levels/schema';

export interface LevelEditorOptions {
  level: LevelDef;
  fileName: string;
  canSaveToDisk: boolean;
  onOpen: (targets: readonly EditorTarget[]) => void;
  onPreview: (target: EditorTarget) => void;
  onClose: () => void;
  onSave: (fileName: string, level: LevelDef) => Promise<LevelSaveResult>;
  onReload: () => void;
}

export class LevelEditor {
  private readonly root: HTMLElement;
  private readonly targetSelect: HTMLSelectElement;
  private readonly xInput: HTMLInputElement;
  private readonly yInput: HTMLInputElement;
  private readonly zInput: HTMLInputElement;
  private readonly rotationInput: HTMLInputElement;
  private readonly saveButton: HTMLButtonElement;
  private readonly reloadButton: HTMLButtonElement;
  private readonly status: HTMLElement;
  private readonly options: LevelEditorOptions;
  /** Last loaded or successfully saved authoring state, even before reload. */
  private authored: LevelDef;
  private original: LevelDef;
  private draft: LevelDef;
  private active = false;
  private dirty = false;
  private hasSavedToDisk = false;

  constructor(parent: HTMLElement, options: LevelEditorOptions) {
    this.options = options;
    this.authored = cloneLevelForEditing(options.level);
    this.original = cloneLevelForEditing(this.authored);
    this.draft = cloneLevelForEditing(this.authored);

    const host = document.createElement('aside');
    host.id = 'level-editor';
    host.setAttribute('aria-label', 'Level editor');
    host.setAttribute('aria-hidden', 'true');
    host.innerHTML = TEMPLATE;
    parent.append(host);
    this.root = host;

    this.targetSelect = mustFind(host, '#editor-target', HTMLSelectElement);
    this.xInput = mustFind(host, '#editor-x', HTMLInputElement);
    this.yInput = mustFind(host, '#editor-y', HTMLInputElement);
    this.zInput = mustFind(host, '#editor-z', HTMLInputElement);
    this.rotationInput = mustFind(host, '#editor-rotation', HTMLInputElement);
    this.saveButton = mustFind(host, '#editor-save', HTMLButtonElement);
    this.reloadButton = mustFind(host, '#editor-reload', HTMLButtonElement);
    this.status = mustFind(host, '#editor-status', HTMLElement);

    this.populateTargets();
    this.targetSelect.addEventListener('change', () => this.selectCurrent());
    mustFind(host, '#editor-apply', HTMLButtonElement).addEventListener('click', () =>
      this.applyExactValues(),
    );
    mustFind(host, '#editor-reset', HTMLButtonElement).addEventListener('click', () =>
      this.resetCurrent(),
    );
    mustFind(host, '#editor-close', HTMLButtonElement).addEventListener('click', () =>
      this.close(),
    );
    mustFind(host, '#editor-export', HTMLButtonElement).addEventListener('click', () =>
      this.exportJson(),
    );
    this.saveButton.addEventListener('click', () => void this.save());
    this.reloadButton.addEventListener('click', options.onReload);

    host.querySelectorAll<HTMLButtonElement>('[data-editor-nudge]').forEach((button) => {
      button.addEventListener('click', () => this.nudge(button.dataset.editorNudge));
    });
    host.querySelectorAll<HTMLButtonElement>('[data-editor-rotate]').forEach((button) => {
      button.addEventListener('click', () => this.rotate(button.dataset.editorRotate));
    });

    if (!options.canSaveToDisk) {
      this.saveButton.disabled = true;
      this.saveButton.title = 'Disk save is available only in the local dev server';
      mustFind(host, '#editor-save-note', HTMLElement).textContent =
        'Disk save needs the local dev server. Export still works.';
    }
    this.refreshButtons();
  }

  get isOpen(): boolean {
    return this.active;
  }

  get isDirty(): boolean {
    return this.dirty;
  }

  get selectedTarget(): EditorTarget | null {
    return readEditorTarget(this.draft, this.targetSelect.value);
  }

  toggle(): void {
    if (this.active) this.close();
    else this.open();
  }

  open(): void {
    if (this.active) return;
    this.original = cloneLevelForEditing(this.authored);
    this.draft = cloneLevelForEditing(this.authored);
    this.dirty = false;
    this.active = true;
    this.populateTargets();
    this.options.onOpen(listEditorTargets(this.draft));
    this.root.classList.add('visible');
    this.root.setAttribute('aria-hidden', 'false');
    if (document.pointerLockElement) document.exitPointerLock();
    this.setStatus('Simulation paused. Preview transforms, then save and reload.');
    this.selectCurrent();
    this.targetSelect.focus();
    this.refreshButtons();
  }

  close(): void {
    if (!this.active) return;
    this.active = false;
    this.root.classList.remove('visible');
    this.root.setAttribute('aria-hidden', 'true');
    this.options.onClose();
  }

  dispose(): void {
    this.close();
    this.root.remove();
  }

  private populateTargets(): void {
    const previous = this.targetSelect?.value;
    const targets = listEditorTargets(this.draft);
    this.targetSelect.replaceChildren();
    for (const target of targets) {
      const option = document.createElement('option');
      option.value = target.key;
      option.textContent = target.label;
      this.targetSelect.append(option);
    }
    const preferred =
      targets.find((target) => target.key === previous) ??
      targets.find((target) => target.kind === 'prop') ??
      targets[0];
    if (preferred) this.targetSelect.value = preferred.key;
  }

  private selectCurrent(): void {
    const target = this.selectedTarget;
    if (!target) return;
    this.showTarget(target);
  }

  private showTarget(target: EditorTarget): void {
    this.xInput.value = formatNumber(target.position[0]);
    this.yInput.value = formatNumber(target.position[1]);
    this.zInput.value = formatNumber(target.position[2]);
    this.rotationInput.disabled = !target.canRotate;
    this.rotationInput.value = target.canRotate
      ? formatNumber(radiansToDegrees(target.rotationY ?? 0))
      : '';
    this.options.onPreview(target);
  }

  private applyExactValues(): void {
    const target = this.selectedTarget;
    if (!target) return;
    const position = [
      this.xInput.valueAsNumber,
      this.yInput.valueAsNumber,
      this.zInput.valueAsNumber,
    ];
    if (!position.every(Number.isFinite)) {
      this.setStatus('X, Y, and Z must all be numbers.', true);
      return;
    }
    const rotationDegrees = this.rotationInput.valueAsNumber;
    if (target.canRotate && !Number.isFinite(rotationDegrees)) {
      this.setStatus('Rotation must be a number.', true);
      return;
    }
    const updated = setEditorTransform(this.draft, target.key, {
      position: [position[0]!, position[1]!, position[2]!],
      ...(target.canRotate ? { rotationY: degreesToRadians(rotationDegrees) } : {}),
    });
    if (!updated) {
      this.setStatus('That transform could not be applied.', true);
      return;
    }
    this.markChanged(updated);
  }

  private nudge(value: string | undefined): void {
    if (!value) return;
    const [axisValue, directionValue] = value.split(':');
    if (!isEditorAxis(axisValue)) return;
    const direction = Number(directionValue);
    if (direction !== -1 && direction !== 1) return;
    const updated = nudgeEditorTarget(
      this.draft,
      this.targetSelect.value,
      axisValue,
      tuning.editor.gridStep * direction,
    );
    if (updated) this.markChanged(updated);
  }

  private rotate(value: string | undefined): void {
    const direction = Number(value);
    if (direction !== -1 && direction !== 1) return;
    const updated = rotateEditorTarget(
      this.draft,
      this.targetSelect.value,
      degreesToRadians(tuning.editor.rotationStepDegrees * direction),
    );
    if (updated) this.markChanged(updated);
  }

  private resetCurrent(): void {
    const original = readEditorTarget(this.original, this.targetSelect.value);
    if (!original) return;
    const updated = setEditorTransform(this.draft, original.key, {
      position: original.position,
      ...(original.rotationY === undefined ? {} : { rotationY: original.rotationY }),
    });
    if (updated) this.markChanged(updated, 'Item reset to the authored transform.');
  }

  private markChanged(
    target: EditorTarget,
    message = 'Preview changed. Save when it looks right.',
  ): void {
    this.dirty = true;
    this.showTarget(target);
    this.setStatus(message);
    this.refreshButtons();
  }

  private async save(): Promise<void> {
    if (!this.options.canSaveToDisk || !this.dirty) return;
    this.saveButton.disabled = true;
    this.setStatus('Checking and saving...');
    try {
      const result = await this.options.onSave(this.options.fileName, this.draft);
      this.authored = cloneLevelForEditing(this.draft);
      this.dirty = false;
      this.hasSavedToDisk = true;
      this.setStatus(`Saved ${result.wrote}. Reload to rebuild physics.`);
    } catch (error) {
      this.setStatus(error instanceof Error ? error.message : String(error), true);
    }
    this.refreshButtons();
  }

  private exportJson(): void {
    const blob = new Blob([`${JSON.stringify(this.draft, null, 2)}\n`], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = this.options.fileName;
    link.click();
    URL.revokeObjectURL(url);
    this.setStatus(`Exported ${this.options.fileName}.`);
  }

  private refreshButtons(): void {
    this.saveButton.disabled = !this.options.canSaveToDisk || !this.dirty;
    this.reloadButton.disabled = !this.hasSavedToDisk;
  }

  private setStatus(message: string, error = false): void {
    this.status.textContent = message;
    this.status.classList.toggle('error', error);
  }
}

function mustFind<T extends HTMLElement>(
  root: HTMLElement,
  selector: string,
  type: { new (): T },
): T {
  const element = root.querySelector(selector);
  if (!(element instanceof type)) throw new Error(`Level editor template is missing ${selector}`);
  return element;
}

function isEditorAxis(value: string | undefined): value is EditorAxis {
  return value === 'x' || value === 'y' || value === 'z';
}

function formatNumber(value: number): string {
  return String(Number(value.toFixed(4)));
}

function degreesToRadians(degrees: number): number {
  return (degrees * Math.PI) / 180;
}

function radiansToDegrees(radians: number): number {
  return (radians * 180) / Math.PI;
}

const TEMPLATE = `
  <div class="editor-heading">
    <div>
      <span class="editor-kicker">F1 DEV TOOL</span>
      <h2>Level editor</h2>
    </div>
    <button id="editor-close" class="editor-close" type="button" aria-label="Close and discard">Close</button>
  </div>
  <p class="editor-warning">Preview only: physics rebuilds after save + reload.</p>

  <label class="editor-field editor-target-field">
    <span>Thing to move</span>
    <select id="editor-target"></select>
  </label>

  <div class="editor-coordinates" aria-label="Exact position">
    <label class="editor-field"><span>X</span><input id="editor-x" type="number" step="any"></label>
    <label class="editor-field"><span>Y</span><input id="editor-y" type="number" step="any"></label>
    <label class="editor-field"><span>Z</span><input id="editor-z" type="number" step="any"></label>
  </div>
  <label class="editor-field">
    <span>Turn (degrees)</span>
    <input id="editor-rotation" type="number" step="any">
  </label>
  <button id="editor-apply" class="editor-button wide" type="button">Apply exact values</button>

  <div class="editor-section">
    <h3>Nudge ${tuning.editor.gridStep} m</h3>
    <div class="editor-nudge-grid">
      <button type="button" data-editor-nudge="x:-1">X -</button>
      <button type="button" data-editor-nudge="x:1">X +</button>
      <button type="button" data-editor-nudge="y:-1">Y -</button>
      <button type="button" data-editor-nudge="y:1">Y +</button>
      <button type="button" data-editor-nudge="z:-1">Z -</button>
      <button type="button" data-editor-nudge="z:1">Z +</button>
    </div>
  </div>

  <div class="editor-section">
    <h3>Turn ${tuning.editor.rotationStepDegrees}&deg;</h3>
    <div class="editor-nudge-grid">
      <button type="button" data-editor-rotate="-1">Turn left</button>
      <button type="button" data-editor-rotate="1">Turn right</button>
    </div>
  </div>

  <div class="editor-actions">
    <button id="editor-reset" class="editor-button secondary" type="button">Reset item</button>
    <button id="editor-export" class="editor-button secondary" type="button">Export JSON</button>
    <button id="editor-save" class="editor-button save" type="button">Save to level</button>
    <button id="editor-reload" class="editor-button reload" type="button">Reload level</button>
  </div>
  <p id="editor-save-note" class="editor-save-note"></p>
  <p id="editor-status" class="editor-status" role="status"></p>
`;
